import gql from 'graphql-tag';
import startCase from 'lodash/startCase';
import React, { useCallback, useContext, useState } from 'react';

import { Select, TextField } from 'src/common/ui/components';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import {
  Staff_Pharmacy as Pharmacy,
  Staff_RecommendationType as PharmacyRecommendation,
} from 'src/generated/gql/graphql';

export enum NotRecommendedReason {
  RefusedTelehealth = 'REFUSED_TELEHEALTH',
  NotAcceptingNewPatients = 'NOT_ACCEPTING_NEW_PATIENTS',
  UncomfortableWithBoulderModel = 'UNCOMFORTABLE_WITH_BOULDER_MODEL',
  IndependentPharmacistRefusal = 'INDEPENDENT_PHARMACIST_REFUSAL',
  MonoproductLimitations = 'MONOPRODUCT_LIMITATIONS',
  RefusedOutsideOfProximity = 'REFUSED_OUTSIDE_OF_PROXIMITY',
  NoMATPrescriptions = 'NO_MAT_PRESCRIPTIONS',
}

export enum RecommendedReason {
  FriendlyPharmacist = 'FRIENDLY_PHARMACIST',
  AlignedWithBoulderModel = 'ALIGNED_WITH_BOULDER_MODEL',
}

const PHARMACY_RECOMMENDATION_MUTATION = gql(`
  mutation UpdatePharmacyRecommendation($pharmacyId: ID!, $recommendation: staff_RecommendationType $recommendationReasons: [String!] $recommendationNote: String) {
    staff_updatePharmacyRecommendation(pharmacyId: $pharmacyId, recommendation: $recommendation, recommendationReasons: $recommendationReasons, recommendationNote: $recommendationNote)
  }
`);

// Functions for converting enums to/from display formats
const startCaseToUpperSnakeCase = (str: string) => str.replace(/\s+/g, '_').toUpperCase();
const upperSnakeCaseToStartCase = (str: string) => startCase(str.toLowerCase());

export const SelectPharmacyRecommendation = ({
  pharmacy,
  updatePharmacyList,
  allowPharmacyUpdates,
}: {
  pharmacy: Pharmacy;
  updatePharmacyList: (pharmacy: Pharmacy) => void;
  allowPharmacyUpdates?: boolean;
}) => {
  const { apolloClient } = useContext(ApolloClientContext);

  const handleRecommendationChange = (_evt: React.SyntheticEvent, val: string | null) => {
    const setVal = val ? startCaseToUpperSnakeCase(val) : null;

    if (setVal === pharmacy.recommendation) {
      return;
    }

    update({ recommendation: setVal });
  };

  const update = useCallback(
    async rec => {
      try {
        updatePharmacyList({ ...pharmacy, ...rec });
        await apolloClient?.mutate({
          mutation: PHARMACY_RECOMMENDATION_MUTATION,
          variables: {
            pharmacyId: pharmacy.id,
            ...rec,
          },
        });
      } catch (e) {
        updatePharmacyList(pharmacy);
        console.error('Error updating pharmacy recommendation');
        console.error(e);
      }
    },
    [apolloClient, pharmacy, updatePharmacyList],
  );

  return (
    <Select
      options={Object.values(PharmacyRecommendation)}
      getOptionLabel={option => upperSnakeCaseToStartCase(option)}
      onInputChange={handleRecommendationChange}
      value={pharmacy.recommendation}
      readOnly={!allowPharmacyUpdates}
    />
  );
};

export const SelectPharmacyRecommendationReason = ({
  pharmacy,
  updatePharmacyList,
  allowPharmacyUpdates,
}: {
  pharmacy: Pharmacy;
  updatePharmacyList: (pharmacy: Pharmacy) => void;
  allowPharmacyUpdates?: boolean;
}) => {
  const { apolloClient } = useContext(ApolloClientContext);

  const handleRecommendationReasonChange = (_evt: React.SyntheticEvent, val: string[] | null) => {
    const setVal = val?.length ? val.map(startCaseToUpperSnakeCase) : null;

    if (setVal === pharmacy.recommendationReasons) {
      return;
    }

    update({ recommendationReasons: setVal });
  };

  const update = useCallback(
    async rec => {
      try {
        updatePharmacyList({ ...pharmacy, ...rec });
        await apolloClient?.mutate({
          mutation: PHARMACY_RECOMMENDATION_MUTATION,
          variables: {
            pharmacyId: pharmacy.id,
            ...rec,
          },
        });
      } catch (e) {
        updatePharmacyList(pharmacy);
        console.error('Error updating pharmacy recommendation');
        console.error(e);
      }
    },
    [apolloClient, pharmacy, updatePharmacyList],
  );

  const recommendationReasons =
    pharmacy.recommendation === PharmacyRecommendation.NotRecommended
      ? Object.values(NotRecommendedReason)
      : Object.values(RecommendedReason);

  return (
    <Select
      multiple
      options={recommendationReasons}
      getOptionLabel={option => upperSnakeCaseToStartCase(option)}
      onChange={handleRecommendationReasonChange}
      value={pharmacy.recommendationReasons ?? []}
      readOnly={!allowPharmacyUpdates}
    />
  );
};

export const PharmacyRecommendationNote = ({
  pharmacy,
  updatePharmacyList,
  allowPharmacyUpdates,
}: {
  pharmacy: Pharmacy;
  updatePharmacyList: (pharmacy: Pharmacy) => void;
  allowPharmacyUpdates?: boolean;
}) => {
  const { apolloClient } = useContext(ApolloClientContext);
  const [note, setNote] = useState<string>(pharmacy.recommendationNote ?? '');

  const handleBlur = useCallback(async () => {
    const trimmedNote = note.trim();
    const normalizedNote = trimmedNote || null;

    if (normalizedNote === pharmacy.recommendationNote) {
      return;
    }

    try {
      updatePharmacyList({ ...pharmacy, recommendationNote: normalizedNote });
      await apolloClient?.mutate({
        mutation: PHARMACY_RECOMMENDATION_MUTATION,
        variables: {
          pharmacyId: pharmacy.id,
          recommendationNote: normalizedNote,
        },
      });
    } catch (e) {
      updatePharmacyList(pharmacy);
      setNote(pharmacy.recommendationNote ?? '');
      console.error('Error updating pharmacy recommendation note');
      console.error(e);
    }
  }, [apolloClient, note, pharmacy, updatePharmacyList]);

  return (
    <TextField
      value={note}
      onChange={e => setNote(e.target.value)}
      onBlur={handleBlur}
      disabled={!allowPharmacyUpdates}
      placeholder="Add a note..."
      multiline
    />
  );
};
